import React, { useCallback, ReactNode } from "react";
import { Button, CircularProgress, ButtonProps } from "@mui/material";


interface CustomButtonProps extends Omit<ButtonProps, 'onClick'> {
  children: ReactNode;
  className?: string;
  variant?: "text" | "outlined" | "contained";
  color?: "inherit" | "primary" | "secondary" | "success" | "error" | "info" | "warning";
  size?: "small" | "medium" | "large";
  fullWidth?: boolean;
  disabled?: boolean;
  startIcon?: ReactNode;
  endIcon?: ReactNode;
  onClick?: (event: React.MouseEvent<HTMLButtonElement>) => void;
  type?: "button" | "submit" | "reset";
  loading?: boolean;
  loadingText?: string;
  loadingPosition?: "start" | "center" | "end";
  loadingIndicator?: ReactNode;
  to?: string;
}

export const CustomButton = React.memo<CustomButtonProps>(
  ({
    children,
    className = "",
    variant = "contained",
    color = "primary",
    size = "medium",
    fullWidth = false,
    disabled = false,
    startIcon,
    endIcon,
    onClick = () => {},
    type = "button",
    loading = false,
    loadingText = "Loading...",
    loadingPosition = "center",
    loadingIndicator = <CircularProgress size={20} color="inherit" />,
    ...props
  }) => {
    const getButtonContent = useCallback(() => {
      if (!loading) {
        return (
          <>
            {startIcon && (
              <span className="button-icon start-icon">{startIcon}</span>
            )}
            <span className="button-text">{children}</span>
            {endIcon && <span className="button-icon end-icon">{endIcon}</span>}
          </>
        );
      }

      switch (loadingPosition) {
        case "start":
          return (
            <div className="button-content">
              {loadingIndicator}
              <span className="loading-text">{loadingText}</span>
            </div>
          );
        case "end":
          return (
            <div className="button-content">
              <span className="loading-text">{loadingText}</span>
              {loadingIndicator}
            </div>
          );
        default: // center
          return <div className="button-content">{loadingIndicator}</div>;
      }
    }, [loading, loadingPosition, loadingIndicator, loadingText, children, startIcon, endIcon]);

    return (
      <Button
        className={`custom-button ${loading ? "loading" : ""} ${className}`}
        variant={variant}
        color={color}
        size={size}
        fullWidth={fullWidth}
        disabled={disabled || loading}
        onClick={onClick}
        type={type}
        {...props}
      >
        {getButtonContent()}
      </Button>
    );
  }
);
